import React, { useContext, useState, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { ApplicationContext } from '@/state/providers';
import Button from '@/components/atoms/Button';
import Close from '@/components/atoms/Icon/Close';
import { AddScroll } from '@/components/utils/DeviceUtils';

import './Overlay.scss';

const propTypes = {
    children: PropTypes.node,
    id: PropTypes.string,
    open: PropTypes.bool,
    onAnimationEndCallback: PropTypes.func,
    onBeforeonAnimationEndCallback: PropTypes.func,
    opaque: PropTypes.bool,
};

const defaultProps = {
    children: null,
    id: null,
    open: false,
    onAnimationEndCallback: null,
    onBeforeonAnimationEndCallback: null,
    opaque: false,
};

const Overlay = (props) => {
    const [data] = useContext(ApplicationContext);
    const [isClosing, setIsClosing] = useState(false);
    const [cancelClose, setCancelClose] = useState(false);

    const {
 children, open, onAnimationEndCallback, onBeforeonAnimationEndCallback, opaque, id,
} = props;

    const ref = React.useRef();
    const refContent = React.useRef();

    const classes = classNames({
        Overlay: true,
        'Overlay--opaque': opaque,
        'Overlay--open': open,
        'Overlay--closing': isClosing,
    });

    const destroyOverlay = () => {
        if (onAnimationEndCallback && !cancelClose) onAnimationEndCallback.call(null);

        setIsClosing(false);
    };

    const hasAnimation = () => getComputedStyle(ref.current)?.animationName !== 'none';

    const onAnimationEnd = (event) => {
        if (isClosing && event.target === ref.current) destroyOverlay();
    };

    const closeOverlay = () => {
        setIsClosing(true);

        setCancelClose(onBeforeonAnimationEndCallback ? onBeforeonAnimationEndCallback.call(null) : false);

        if (!hasAnimation()) destroyOverlay();
    };

    const childrenWithProps = React.Children.map(children, (child) => React.cloneElement(child, {
            closeOverlay: () => {
                closeOverlay();
            },
        }));

    useEffect(() => {
        ref.current.addEventListener('animationend', onAnimationEnd);

        return () => ref.current.removeEventListener('animationend', onAnimationEnd);
    });

    useEffect(() => {
        if (!open) {
            destroyOverlay();
        } else if (refContent.current) {
            refContent.current.scrollTop = 0;
        }
    }, [open]);

    useEffect(() => {
        AddScroll(refContent.current);
    }, []);

    return (
        <div ref={ref} className={classes} id={id}>
            <div className="Overlay__inner">
                <div className="Overlay__close">
                    <Button variant="icon-light" largeHitbox="left" icon={<Close />} type="button" onClick={closeOverlay}>
                        {data.overlay.text.cta.close}
                    </Button>
                </div>
                <div className="Overlay__content" ref={refContent}>
                    {childrenWithProps}
                </div>
            </div>
        </div>
    );
};

Overlay.propTypes = propTypes;
Overlay.defaultProps = defaultProps;

export default Overlay;
